import { apiService } from './api';
import type { UserSubscription, CreditBalance, PlanFeatures } from '../types';

// Updated UsageSummary with credit tracking
export interface UsageSummary {
  planName?: string; // NEW: Plan name from snapshot
  workspace: {
    daily: {
      requests: number;
      requestsLimit: number;
      requestsPercent: number;
      tokens: number;
      tokensLimit: number;
      tokensPercent: number;
      costCents: number;
      // costLimitCents, costPercent REMOVED - using credits now
    };
    monthly: {
      tokens: number;
      tokensLimit: number;
      tokensPercent: number;
      costCents: number;
      // NEW: Credit tracking
      creditsUsed: number;
      creditsLimit: number;
      creditsPercent: number;
    };
  };
  user: {
    daily: {
      requests: number;
      tokens: number;
      costCents: number;
    };
  };
}

// Updated: WorkspaceLimits now read-only from plan snapshot
export interface WorkspaceLimits {
  workspaceId: string;
  planName?: string; // NEW
  requestsPerMinute: number;
  requestsPerHour: number;
  requestsPerDay: number;
  tokensPerDay: number;
  tokensPerMonth: number;
  tokensPerMinute: number; // NEW
  creditsPerMonth: number; // NEW
  features?: PlanFeatures; // NEW
  // REMOVED: isUnlimited, isActive, customLimits, costLimitPerDayCents, costLimitPerMonthCents
}

export interface CurrentUsage {
  daily: {
    requests: number;
    tokens: number;
    costCents: number;
  };
  monthly: {
    tokens: number;
    costCents: number;
  };
}
export interface UsageAnalytics {
  summary: {
    totalRequests: number;
    totalTokens: number;
    totalCostCents: number;
    avgTokensPerRequest: number;
    avgCostPerRequest: number;
  };
  dailyUsage: Array<{
    date: string;
    requests: number;
    tokens: number;
    costCents: number;
  }>;
  providerBreakdown: Record<string, { requests: number; tokens: number; costCents: number }>;
  modelBreakdown: Record<string, { requests: number; tokens: number; costCents: number }>;
  userBreakdown: Array<{ userId: string; requests: number; tokens: number; costCents: number }>;
}

export interface UsageAlert {
  id: string;
  tenantId: string;
  workspaceId: string;
  userId?: string;
  alertType: 'warning' | 'limit_reached' | 'limit_exceeded';
  limitType: string;
  currentValue: number;
  limitValue: number;
  percentageUsed: number;
  message?: string;
  acknowledged: boolean;
  createdAt: string;
}

class UsageService {
  /**
   * Get usage summary for a workspace
   */
  async getUsageSummary(workspaceId: string): Promise<{ success: boolean; data: UsageSummary }> {
    return apiService.get(`/usage/summary/${workspaceId}`);
  }

  /**
   * Get detailed workspace analytics
   */
  async getWorkspaceAnalytics(
    workspaceId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    success: boolean;
    data: {
      analytics: UsageAnalytics;
      limits: WorkspaceLimits;
      currentUsage: CurrentUsage;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const queryString = params.toString();
    const url = `/usage/workspace/${workspaceId}${queryString ? `?${queryString}` : ''}`;

    return apiService.get(url);
  }

  /**
   * Get user analytics
   */
  async getUserAnalytics(
    userId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    success: boolean;
    data: {
      summary: {
        totalRequests: number;
        totalTokens: number;
        totalCostCents: number;
      };
      workspaceBreakdown: Array<{
        workspaceId: string;
        requests: number;
        tokens: number;
        costCents: number;
      }>;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const queryString = params.toString();
    const url = `/usage/user/${userId || ''}${queryString ? `?${queryString}` : ''}`;

    return apiService.get(url);
  }

  /**
   * Get workspace limits (read-only, from plan snapshot)
   */
  async getWorkspaceLimits(workspaceId: string): Promise<{ success: boolean; data: WorkspaceLimits }> {
    return apiService.get(`/usage/limits/${workspaceId}`);
  }

  /**
   * Update workspace limits - DEPRECATED
   * This method now returns 410 Gone from the backend.
   * Limits are managed via plans, not per-workspace.
   * @deprecated Use plan management instead
   */
  async updateWorkspaceLimits(
    _workspaceId: string,
    _limits: Partial<WorkspaceLimits>
  ): Promise<{ success: boolean; data: WorkspaceLimits }> {
    void _workspaceId;
    void _limits;
    console.warn('updateWorkspaceLimits is deprecated. Limits are now managed via plans.');
    throw new Error('Workspace limits are now managed via plans. Use plan management instead.');
  }

  /**
   * Get subscription info for current user's tenant
   * NEW: Fetches credit balance and subscription status
   */
  async getMySubscription(): Promise<{ success: boolean; data: UserSubscription }> {
    return apiService.get('/usage/subscription');
  }

  /**
   * Get credit balance for current workspace
   * NEW: Dedicated endpoint for credit info
   */
  async getCreditBalance(workspaceId: string): Promise<{ success: boolean; data: CreditBalance }> {
    return apiService.get(`/usage/credits/${workspaceId}`);
  }

  /**
   * Get usage alerts
   */
  async getAlerts(workspaceId: string, limit?: number): Promise<{ success: boolean; data: UsageAlert[]; count: number }> {
    const url = limit ? `/usage/alerts/${workspaceId}?limit=${limit}` : `/usage/alerts/${workspaceId}`;
    return apiService.get(url);
  }

  /**
   * Acknowledge an alert
   */
  async acknowledgeAlert(alertId: string): Promise<{ success: boolean; message: string }> {
    return apiService.post(`/usage/alerts/${alertId}/acknowledge`);
  }

  /**
   * Format cost in USD
   */
  formatCost(cents: number | null | undefined): string {
    const safeCents = typeof cents === 'number' && Number.isFinite(cents) ? cents : 0;
    const dollars = safeCents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  /**
   * Format number with Indian numbering system (XX,XX,XXX)
   */
  private formatIndianNumber(num: number | null | undefined, decimals: number = 0): string {
    const safeNum = typeof num === 'number' && Number.isFinite(num) ? num : 0;
    const parts = safeNum.toFixed(decimals).split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Indian numbering: first group of 3 from right, then groups of 2
    let result = '';
    let count = 0;

    for (let i = integerPart.length - 1; i >= 0; i--) {
      if (count === 3 || (count > 3 && (count - 3) % 2 === 0)) {
        result = ',' + result;
      }
      result = integerPart[i] + result;
      count++;
    }

    return decimalPart ? `${result}.${decimalPart}` : result;
  }

  /**
   * Format large numbers with Indian numbering system
   * Uses Lakh (1,00,000) and Crore (1,00,00,000) system
   */
  formatNumber(num: number): string {
    const safeNum = typeof num === 'number' && Number.isFinite(num) ? num : 0;
    if (safeNum >= 10000000) {
      // 1 Crore and above
      return `${this.formatIndianNumber(safeNum / 10000000, 2)} Cr`;
    }
    if (safeNum >= 100000) {
      // 1 Lakh and above
      return `${this.formatIndianNumber(safeNum / 100000, 2)} L`;
    }
    if (safeNum >= 1000) {
      // Use Indian comma formatting for thousands
      return this.formatIndianNumber(safeNum, 0);
    }
    return safeNum.toString();
  }

  /**
   * Get color based on percentage
   */
  getUsageColor(percent: number): string {
    if (percent >= 100) return 'red';
    if (percent >= 80) return 'orange';
    if (percent >= 60) return 'yellow';
    return 'green';
  }
}

export const usageService = new UsageService();
